import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAllSportsbooks } from '../data/stateSportsbooksApi';

/**
 * Returns the cached sportsbook list and a resolver that maps a sportsbook
 * code (e.g. "HardRockNJ") to its human-readable name from the DB (e.g.
 * "Hard Rock NJ"), falling back to the code when the lookup misses. Shared
 * React Query cache means only one fetch fires across all consumers.
 */
export function useSportsbookNames() {
  const { data: allSportsbooks = [] } = useQuery({
    queryKey: ['all-sportsbooks'],
    queryFn: fetchAllSportsbooks,
    staleTime: Infinity,
  });

  const namesByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const sb of allSportsbooks) {
      if (sb.sportsbookCode) {
        map.set(sb.sportsbookCode.toLowerCase(), sb.name);
      }
    }
    return map;
  }, [allSportsbooks]);

  const getName = useCallback(
    (sportsbookCode: string): string => {
      if (!sportsbookCode) return sportsbookCode;
      return namesByCode.get(sportsbookCode.toLowerCase()) || sportsbookCode;
    },
    [namesByCode],
  );

  return { getName, allSportsbooks };
}
